import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Globe, 
  Clock, 
  MapPin, 
  ArrowUp, 
  ArrowDown,
  Calendar
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import axios from 'axios';
import { format, subDays } from 'date-fns';

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [isLoading, setIsLoading] = useState(true);
  const [overview, setOverview] = useState({
    totalVisitors: 0,
    uniqueVisitors: 0,
    avgTimeOnSite: '0m 0s',
    bounceRate: '0%',
    pagesPerVisit: 0
  });
  const [visitorTrend, setVisitorTrend] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [browserData, setBrowserData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [referrerData, setReferrerData] = useState([]);
  const [keywordsData, setKeywordsData] = useState([]);
  const [currentSiteId, setCurrentSiteId] = useState(null);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState(null);
  
  // Load list of sites when component mounts
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await axios.get('/api/sites');
        setSites(response.data);
        
        // Set the current site to the first site if not already set
        if (response.data.length > 0 && !currentSiteId) {
          setCurrentSiteId(response.data[0].id);
        }
      } catch (err) {
        console.error('Error fetching sites:', err);
        setError('Failed to load your sites. Please try again later.');
      }
    };
    
    fetchSites();
  }, [currentSiteId]);
  
  // Load data when component mounts, time range changes, or site changes
  useEffect(() => {
    if (!currentSiteId) return;
    
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Overview data (totals, rates, etc.)
        const overviewResponse = await axios.get(`/api/analytics/${currentSiteId}/overview?timeRange=${timeRange}`);
        const overviewData = overviewResponse.data;
        
        // Format data for display
        const formattedOverview = {
          totalVisitors: overviewData.overview.total_visits,
          uniqueVisitors: overviewData.overview.unique_visitors,
          avgTimeOnSite: formatTimeOnSite(overviewData.overview.avg_time_on_site),
          bounceRate: `${Math.round(overviewData.overview.bounce_rate)}%`,
          pagesPerVisit: overviewData.overview.page_views / overviewData.overview.total_visits || 0
        };
        
        setOverview(formattedOverview);
        setVisitorTrend(overviewData.trend);
        
        // Device breakdown
        const devicesResponse = await axios.get(`/api/analytics/${currentSiteId}/devices?timeRange=${timeRange}`);
        const formattedDevices = devicesResponse.data.map(item => ({
          name: item.type.charAt(0).toUpperCase() + item.type.slice(1),
          value: item.visitors
        }));
        
        setDeviceData(formattedDevices);
        
        // Browser data
        const browsersResponse = await axios.get(`/api/analytics/${currentSiteId}/browsers?timeRange=${timeRange}`);
        const formattedBrowsers = browsersResponse.data.map(item => ({
          name: item.name,
          users: item.visitors
        }));
        
        setBrowserData(formattedBrowsers);
        
        // Location data
        const locationsResponse = await axios.get(`/api/analytics/${currentSiteId}/locations?timeRange=${timeRange}`);
        setLocationData(locationsResponse.data);
        
        // Referrer data
        const referrersResponse = await axios.get(`/api/analytics/${currentSiteId}/referrers?timeRange=${timeRange}`);
        setReferrerData(referrersResponse.data);

        // Keywords data
        const keywordsResponse = await axios.get(`/api/analytics/${currentSiteId}/keywords?timeRange=${timeRange}`);
        setKeywordsData(keywordsResponse.data);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [timeRange, currentSiteId]);
  
  // Format seconds into "Xm Ys" format
  const formatTimeOnSite = (seconds) => {
    if (!seconds) return '0m 0s';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };
  
  const getTimeRangeDays = () => {
    switch(timeRange) {
      case '1day': return 1;
      case '7days': return 7;
      case '30days': return 30;
      case '90days': return 90;
      default: return 7;
    }
  };
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  
  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="spinner"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center bg-red-50 p-6 rounded-lg border border-red-200 max-w-md">
          <h2 className="text-lg font-medium text-red-800 mb-2">Error Loading Data</h2>
          <p className="text-red-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // No sites state
  if (sites.length === 0) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center bg-yellow-50 p-6 rounded-lg border border-yellow-200 max-w-md">
          <h2 className="text-lg font-medium text-yellow-800 mb-2">No Sites Found</h2>
          <p className="text-yellow-700">You need to add a site before you can see analytics data.</p>
          <a 
            href="/sites" 
            className="mt-4 inline-block px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Add Your First Site
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        
        <div className="flex items-center space-x-4">
          {sites.length > 1 && (
            <select
              className="border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              value={currentSiteId}
              onChange={(e) => setCurrentSiteId(e.target.value)}
            >
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          )}
          
          <div className="flex items-center">
            <label htmlFor="time-range" className="text-sm font-medium text-gray-700 mr-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Time Range:
            </label>
            <select
              id="time-range"
              className="border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="1day">Last 24 hours</option>
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-blue-100 text-blue-700 mr-4">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Visitors</p>
              <p className="text-xl font-semibold">{overview.totalVisitors.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-green-100 text-green-700 mr-4">
              <Globe className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Unique Visitors</p>
              <p className="text-xl font-semibold">{overview.uniqueVisitors.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-purple-100 text-purple-700 mr-4">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Avg. Time on Site</p>
              <p className="text-xl font-semibold">{overview.avgTimeOnSite}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-red-100 text-red-700 mr-4">
              <ArrowUp className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Bounce Rate</p>
              <p className="text-xl font-semibold">{overview.bounceRate}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 rounded-md bg-yellow-100 text-yellow-700 mr-4">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Pages / Visit</p>
              <p className="text-xl font-semibold">{overview.pagesPerVisit.toFixed(1)}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Visitor Trends</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={visitorTrend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="visitors" 
                  name="Visitors"
                  stroke="#6366F1" 
                  fill="#EEF2FF" 
                  strokeWidth={2} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Device Breakdown</h2>
          {deviceData.length > 0 ? (
            <div className="h-64 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {deviceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">No device data available</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Top Browsers</h2>
          {browserData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={browserData} margin={{ top: 5, right: 30, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip />
                  <Bar dataKey="users" name="Users" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">No browser data available</p>
            </div>
          )}
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Top Locations</h2>
          {locationData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  layout="vertical" 
                  data={locationData.slice(0, 5)} 
                  margin={{ top: 5, right: 30, bottom: 5, left: 70 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#9CA3AF" />
                  <YAxis dataKey="name" type="category" stroke="#9CA3AF" />
                  <Tooltip />
                  <Bar dataKey="visitors" name="Visitors" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">No location data available</p>
            </div>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Top Referrers</h2>
          {referrerData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={referrerData.slice(0, 5)} margin={{ top: 5, right: 30, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="source" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="visitors" name="Visitors" fill="#ff7c43">
                    {referrerData.map((entry, index) => {
                      let color;
                      switch(entry.type) {
                        case 'search': color = '#8884d8'; break;
                        case 'social': color = '#82ca9d'; break;
                        case 'direct': color = '#ffc658'; break;
                        default: color = '#ff7c43';
                      }
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">No referrer data available</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Top Search Keywords</h2>
          {keywordsData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={keywordsData.slice(0, 10)} margin={{ top: 5, right: 30, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="keyword" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="visitors" name="Visitors" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">No search keyword data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;